import React, { useState } from 'react';
import { User, Mail, Phone, Calendar, Clock } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface ContactFormProps {
  onClose: () => void;
}

interface ContactFormData {
  civilite: string;
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  message: string;
  date_contact: string;
  heure_contact: string;
}

export default function ContactForm({ onClose }: ContactFormProps) {
  const [formData, setFormData] = useState<ContactFormData>({
    civilite: 'M',
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    message: '',
    date_contact: '',
    heure_contact: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 9; hour <= 18; hour++) {
      for (let minute of ['00', '30']) {
        if (hour === 18 && minute === '30') continue;
        const time = `${hour.toString().padStart(2, '0')}:${minute}`;
        slots.push(time);
      }
    }
    return slots;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      console.log('Submitting contact form:', formData);

      const response = await fetch('/api/contact-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        console.error('Erreur lors de l’enregistrement:', result.error);
        throw new Error(result.error || 'Erreur lors de l’enregistrement de votre demande');
      }

      console.log('Contact request enregistrée avec succès dans la base');
      toast.success('Votre demande de contact a été envoyée avec succès !');
      onClose();

    } catch (error) {
      console.error('Erreur lors de l’envoi du formulaire:', error);
      toast.error(error instanceof Error ? error.message : 'Une erreur est survenue lors de l’envoi');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white/80 backdrop-blur-xl p-8 rounded-xl shadow-[0_8px_32px_rgba(42,67,101,0.15)] max-w-2xl w-full mx-auto
      transform hover:scale-[1.01] transition-all duration-300
      before:content-[''] before:absolute before:inset-0
      before:bg-gradient-to-b before:from-white/30 before:to-[#2A4365]/10 before:pointer-events-none
      [transform-style:preserve-3d] hover:[transform:perspective(1000px)_rotateX(1deg)]
      after:content-[''] after:absolute after:inset-0
      after:bg-gradient-to-r after:from-[#2A4365]/10 after:via-[#4A5568]/10 after:to-[#C4A052]/10 
      after:pointer-events-none relative overflow-hidden">
      <h2 className="text-2xl font-bold text-[#2A4365] mb-6 relative z-10">
        Demande de contact
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Civilité
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="civilite"
                  value="M"
                  checked={formData.civilite === 'M'}
                  onChange={(e) => setFormData({ ...formData, civilite: e.target.value })}
                  className="h-4 w-4 text-[#2A4365] focus:ring-[#2A4365] border-gray-300"
                />
                <span className="ml-2">M.</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="civilite"
                  value="Mme"
                  checked={formData.civilite === 'Mme'}
                  onChange={(e) => setFormData({ ...formData, civilite: e.target.value })}
                  className="h-4 w-4 text-[#2A4365] focus:ring-[#2A4365] border-gray-300"
                />
                <span className="ml-2">Mme</span>
              </label>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                className="pl-10 w-full rounded-lg border-gray-300 shadow-sm 
                  focus:border-[#2A4365] focus:ring-[#2A4365]
                  hover:border-[#2A4365]/50 transition-colors"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Prénom
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                value={formData.prenom}
                onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                className="pl-10 w-full rounded-lg border-gray-300 shadow-sm 
                  focus:border-[#2A4365] focus:ring-[#2A4365]
                  hover:border-[#2A4365]/50 transition-colors"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="pl-10 w-full rounded-lg border-gray-300 shadow-sm 
                  focus:border-[#2A4365] focus:ring-[#2A4365]
                  hover:border-[#2A4365]/50 transition-colors"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Téléphone
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="tel"
                value={formData.telephone}
                onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                className="pl-10 w-full rounded-lg border-gray-300 shadow-sm 
                  focus:border-[#2A4365] focus:ring-[#2A4365]
                  hover:border-[#2A4365]/50 transition-colors"
                required
              />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Message
          </label>
          <textarea
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            rows={4}
            className="w-full rounded-lg border-gray-300 shadow-sm 
              focus:border-[#2A4365] focus:ring-[#2A4365]
              hover:border-[#2A4365]/50 transition-colors"
            required
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date de contact souhaitée
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="date"
                value={formData.date_contact}
                onChange={(e) => setFormData({ ...formData, date_contact: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                className="pl-10 w-full rounded-lg border-gray-300 shadow-sm 
                  focus:border-[#2A4365] focus:ring-[#2A4365]
                  hover:border-[#2A4365]/50 transition-colors"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Heure de contact souhaitée
            </label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <select
                value={formData.heure_contact}
                onChange={(e) => setFormData({ ...formData, heure_contact: e.target.value })}
                className="pl-10 w-full rounded-lg border-gray-300 shadow-sm 
                  focus:border-[#2A4365] focus:ring-[#2A4365]
                  hover:border-[#2A4365]/50 transition-colors"
                required
              >
                <option value="">Choisir une heure</option>
                {generateTimeSlots().map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-4 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2 text-base font-medium rounded-xl text-white
              bg-gradient-to-r from-[#2A4365] via-[#4A5568] to-[#C4A052]
              hover:from-[#1A3355] hover:via-[#3A4558] hover:to-[#B49042]
              focus:outline-none focus:ring-2 focus:ring-[#2A4365] focus:ring-offset-2
              transform transition-all duration-300 hover:scale-105
              shadow-lg hover:shadow-xl hover:shadow-[#2A4365]/20"
          >
            {isSubmitting ? 'Envoi en cours...' : 'Envoyer'}
          </button>
        </div>
      </form>
    </div>
  );
}